import {Button, Stack, Typography, useTheme} from "@mui/joy";
import {useSessionCookie} from "../../hooks/useSessionCookie";
import React, {useEffect} from "react";
import {useRouteError} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {RotateCw, ServerCrash} from "lucide-react";
import {appRoute} from "../../utils/routes";
import GuestNavbar from "../Layout/GuestNavbar";

/**
 * Shown when a page throws while rendering, in place of React Router's developer error screen.
 * Wired as `errorElement` on the root route of every router.
 *
 * It uses the guest navbar for everyone (signed-in visitors get its "Go to Dashboard" button)
 * rather than the signed-in layout: if the crash came from that layout, rendering it here would
 * just throw again.
 */
const ServerError = () => {
    const error = useRouteError()
    const theme = useTheme()
    const {t} = useTranslation()
    const {userSession} = useSessionCookie()
    const homePath = userSession ? appRoute() : "/"

    // The details stay in the console for debugging; visitors get a plain message.
    useEffect(() => {
        console.error(error)
    }, [error])

    return <Stack minHeight={"100dvh"}>
        <GuestNavbar/>

        <Stack flex={1} minHeight={{xs: "60dvh", md: "70dvh"}} justifyContent={"center"} alignItems={"center"} gap={1}
               padding={3}>
            <ServerCrash style={{color: theme.palette.danger['500'], fontSize: 90}}/>

            <Typography level={"h2"} textAlign={"center"}>
                {t('serverErrorTitle')}
            </Typography>

            <Typography level={"body-lg"} textAlign={"center"} maxWidth={"32rem"}>
                {t('serverErrorDescription')}
            </Typography>

            <Stack direction={{xs: "column", sm: "row"}} gap={1.5} marginTop={1}>
                <Button onClick={() => window.location.reload()} startDecorator={<RotateCw/>} size={"lg"}
                        sx={{width: "14rem"}}>
                    {t('serverErrorReloadActionLabel')}
                </Button>
                {/* A full page load, so the app starts from a clean state. */}
                <Button component={"a"} href={homePath} variant={"outlined"} color={"neutral"} size={"lg"}
                        sx={{width: "14rem"}}>
                    {t('notFoundActionLabel')}
                </Button>
            </Stack>
        </Stack>
    </Stack>
}

export default ServerError
